import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, UserCog, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

/**
 * Persistent orange banner shown whenever the app is running with an
 * admin's impersonation token. One click on "Return to admin" restores
 * the original admin session — no re-login needed.
 *
 * Rendered at the very top of the app so it never hides behind
 * navigation, and uses aria-live so screen readers announce the state.
 */
export function ImpersonationBanner() {
  const { isImpersonating, user, stopImpersonation } = useAuth();
  const [leaving, setLeaving] = useState(false);

  if (!isImpersonating || !user) return null;

  const handleReturn = async () => {
    try {
      setLeaving(true);
      await stopImpersonation();
      toast.success("Returned to your admin account");
      // Push the admin back to the admin dashboard as the natural landing.
      window.location.assign("/admin");
    } catch (err) {
      toast.error("Failed to return to admin account. Please log out and back in.");
      setLeaving(false);
    }
  };

  const roleText = Array.isArray(user.roles) && user.roles.length
    ? user.roles.join(", ")
    : "no role";

  return (
    <div
      aria-live="polite"
      role="status"
      data-testid="impersonation-banner"
      className="sticky top-0 z-[100] w-full bg-amber-500 text-amber-950 border-b border-amber-700 shadow-md"
    >
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <UserCog className="w-4 h-4 shrink-0" />
          <span>
            <strong>Impersonation active</strong> — signed in as{" "}
            <strong data-testid="impersonation-target-name">{user.name || user.email}</strong>{" "}
            <span className="opacity-75">({roleText})</span>. All actions are logged.
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReturn}
          disabled={leaving}
          className="bg-white/90 hover:bg-white border-amber-700 text-amber-900"
          data-testid="impersonation-return-btn"
        >
          {leaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Returning...
            </>
          ) : (
            <>
              <LogOut className="w-4 h-4 mr-2" />
              Return to admin
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export default ImpersonationBanner;
